"use client";

import { useEffect, useState } from "react";

type GlowBusinessMapEditorProps = {
  businessMap: any;
  onSave: (nextBusinessMap: any) => void | Promise<void>;
};

type CollectionKey =
  | "salesChannels"
  | "offerTypes"
  | "idealCustomers"
  | "keyLinks";

function cloneBusinessMap(businessMap: any) {
  return JSON.parse(JSON.stringify(businessMap || {}));
}

function normaliseDraft(businessMap: any) {
  const next = cloneBusinessMap(businessMap);

  return {
    ...next,
    sellingModel: {
      value: "",
      evidence: "",
      status: "needs_confirmation",
      ...(next.sellingModel || {}),
    },
    physicalPresence: {
      value: "",
      characteristics: [],
      status: "needs_confirmation",
      ...(next.physicalPresence || {}),
    },
    salesChannels: Array.isArray(next.salesChannels)
      ? next.salesChannels
      : [],
    offerTypes: Array.isArray(next.offerTypes)
      ? next.offerTypes
      : [],
    idealCustomers: Array.isArray(next.idealCustomers)
      ? next.idealCustomers
      : [],
    keyLinks: Array.isArray(next.keyLinks)
      ? next.keyLinks
      : [],
  };
}

const inputClassName =
  "w-full rounded-2xl border border-[#cddbd6] bg-white px-4 py-3 text-sm font-light text-[#092723] outline-none transition focus:border-[#007c7a]";

const smallInputClassName =
  "w-full rounded-xl border border-[#cddbd6] bg-white px-3 py-2 text-sm font-light text-[#092723] outline-none transition focus:border-[#007c7a]";

function collectionLabel(collection: CollectionKey) {
  return {
    salesChannels: "Sales and booking channels",
    offerTypes: "Products, services and offers",
    idealCustomers: "Ideal customers",
    keyLinks: "Important business links",
  }[collection];
}

function newCollectionItem(collection: CollectionKey) {
  if (collection === "salesChannels") {
    return {
      name: "",
      type: "website",
      url: "",
      evidence: "Added by the business owner.",
      status: "confirmed_by_you",
    };
  }

  if (collection === "offerTypes") {
    return {
      name: "",
      type: "service",
      status: "confirmed_by_you",
    };
  }

  if (collection === "idealCustomers") {
    return {
      label: "",
      reason: "",
      status: "confirmed_by_you",
    };
  }

  return {
    label: "",
    url: "",
    type: "website",
    status: "confirmed_by_you",
  };
}

export default function GlowBusinessMapEditor({
  businessMap,
  onSave,
}: GlowBusinessMapEditorProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() =>
    normaliseDraft(businessMap),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!open) {
      setDraft(normaliseDraft(businessMap));
    }
  }, [businessMap, open]);

  function openEditor() {
    setDraft(normaliseDraft(businessMap));
    setSaveError("");
    setOpen(true);
  }

  function updateObjectField(
    objectName: "sellingModel" | "physicalPresence",
    field: string,
    value: string,
  ) {
    setDraft((current: any) => ({
      ...current,
      [objectName]: {
        ...(current[objectName] || {}),
        [field]: value,
        status: "confirmed_by_you",
      },
    }));
  }

  function updateCollectionItem(
    collection: CollectionKey,
    index: number,
    field: string,
    value: string,
  ) {
    setDraft((current: any) => ({
      ...current,
      [collection]: current[collection].map(
        (item: any, itemIndex: number) =>
          itemIndex === index
            ? {
                ...item,
                [field]: value,
                status: "confirmed_by_you",
              }
            : item,
      ),
    }));
  }

  function confirmCollectionItem(
    collection: CollectionKey,
    index: number,
  ) {
    setDraft((current: any) => ({
      ...current,
      [collection]: current[collection].map(
        (item: any, itemIndex: number) =>
          itemIndex === index
            ? {
                ...item,
                status: "confirmed_by_you",
              }
            : item,
      ),
    }));
  }

  function addCollectionItem(collection: CollectionKey) {
    setDraft((current: any) => ({
      ...current,
      [collection]: [
        ...current[collection],
        newCollectionItem(collection),
      ],
    }));
  }

  function removeCollectionItem(
    collection: CollectionKey,
    index: number,
  ) {
    setDraft((current: any) => ({
      ...current,
      [collection]: current[collection].filter(
        (_item: any, itemIndex: number) => itemIndex !== index,
      ),
    }));
  }

  async function saveMap() {
    setSaving(true);
    setSaveError("");

    try {
      await onSave({
        ...draft,
        ownerEditedAt: new Date().toISOString(),
      });

      setOpen(false);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Glow could not save your Business Map.",
      );
    } finally {
      setSaving(false);
    }
  }

  function renderStatusButton(
    collection: CollectionKey,
    item: any,
    index: number,
  ) {
    const confirmed = item?.status === "confirmed_by_you";

    return (
      <button
        type="button"
        onClick={() =>
          confirmCollectionItem(collection, index)
        }
        disabled={confirmed}
        className={`rounded-xl border px-3 py-2 text-xs font-normal transition ${
          confirmed
            ? "cursor-default border-[#b8dfd2] bg-[#eaf8f3] text-[#007c7a]"
            : "border-[#bcd0ca] bg-white text-[#007c7a] hover:border-[#007c7a]"
        }`}
      >
        {confirmed ? "Confirmed" : "Correct"}
      </button>
    );
  }

  return (
    <div className="mt-6">
      {!open ? (
        <button
          type="button"
          onClick={openEditor}
          className="w-full rounded-3xl border border-[#bcd0ca] bg-[#f4fbf8] px-5 py-4 text-sm font-normal text-[#007c7a] transition hover:border-[#007c7a] hover:bg-[#eaf8f3]"
        >
          Improve this map
        </button>
      ) : (
        <section className="rounded-3xl border border-[#bcd0ca] bg-[#f4fbf8] p-5 md:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-light uppercase tracking-[0.18em] text-[#007c7a]">
                Teach Glow about your business
              </p>

              <h3 className="mt-2 text-2xl font-light text-[#092723]">
                Confirm, correct or add what Glow missed.
              </h3>

              <p className="mt-2 max-w-3xl text-sm font-light leading-6 text-[#52635f]">
                Anything you edit or add becomes confirmed business
                memory for future Glow work.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded-xl border border-[#bcd0ca] bg-white px-4 py-2 text-sm font-light text-[#52635f]"
            >
              Close
            </button>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <label className="rounded-3xl border border-[#d7e5df] bg-white p-5">
              <span className="text-sm font-normal text-[#092723]">
                How the business sells
              </span>

              <textarea
                value={draft.sellingModel.value || ""}
                onChange={(event) =>
                  updateObjectField(
                    "sellingModel",
                    "value",
                    event.target.value,
                  )
                }
                rows={3}
                className={`mt-3 ${inputClassName}`}
                placeholder="Physical shop plus online appointments"
              />
            </label>

            <label className="rounded-3xl border border-[#d7e5df] bg-white p-5">
              <span className="text-sm font-normal text-[#092723]">
                Physical presence
              </span>

              <textarea
                value={draft.physicalPresence.value || ""}
                onChange={(event) =>
                  updateObjectField(
                    "physicalPresence",
                    "value",
                    event.target.value,
                  )
                }
                rows={3}
                className={`mt-3 ${inputClassName}`}
                placeholder="One customer-facing location in Dublin"
              />
            </label>
          </div>

          {(
            [
              "salesChannels",
              "offerTypes",
              "idealCustomers",
              "keyLinks",
            ] as CollectionKey[]
          ).map((collection) => (
            <div
              key={collection}
              className="mt-5 rounded-3xl border border-[#d7e5df] bg-white p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-normal text-[#092723]">
                    {collectionLabel(collection)}
                  </h4>

                  <p className="mt-1 text-xs font-light text-[#52635f]">
                    Correct Glow’s findings or add anything missing.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => addCollectionItem(collection)}
                  className="rounded-xl border border-[#007c7a] bg-white px-4 py-2 text-xs font-normal text-[#007c7a] transition hover:bg-[#f4fbf8]"
                >
                  Add another
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {draft[collection].length === 0 && (
                  <p className="rounded-2xl bg-[#fbf8f3] p-4 text-sm font-light text-[#52635f]">
                    Glow did not find anything here yet.
                  </p>
                )}

                {draft[collection].map(
                  (item: any, index: number) => (
                    <div
                      key={`${collection}-${index}`}
                      className="rounded-2xl bg-[#fbf8f3] p-4"
                    >
                      <div className="grid gap-3 md:grid-cols-2">
                        <input
                          value={
                            collection === "idealCustomers" ||
                            collection === "keyLinks"
                              ? item.label || ""
                              : item.name || ""
                          }
                          onChange={(event) =>
                            updateCollectionItem(
                              collection,
                              index,
                              collection === "idealCustomers" ||
                                collection === "keyLinks"
                                ? "label"
                                : "name",
                              event.target.value,
                            )
                          }
                          className={smallInputClassName}
                          placeholder={
                            collection === "idealCustomers"
                              ? "Ideal customer group"
                              : collection === "keyLinks"
                                ? "Link label"
                                : collection === "offerTypes"
                                  ? "Product or service"
                                  : "Sales or booking channel"
                          }
                        />

                        {collection !== "idealCustomers" && (
                          <input
                            value={
                              collection === "keyLinks"
                                ? item.url || ""
                                : item.type || ""
                            }
                            onChange={(event) =>
                              updateCollectionItem(
                                collection,
                                index,
                                collection === "keyLinks"
                                  ? "url"
                                  : "type",
                                event.target.value,
                              )
                            }
                            className={smallInputClassName}
                            placeholder={
                              collection === "keyLinks"
                                ? "https://..."
                                : "Type"
                            }
                          />
                        )}

                        {collection === "salesChannels" && (
                          <input
                            value={item.url || ""}
                            onChange={(event) =>
                              updateCollectionItem(
                                collection,
                                index,
                                "url",
                                event.target.value,
                              )
                            }
                            className={smallInputClassName}
                            placeholder="Channel or booking URL"
                          />
                        )}

                        {collection === "idealCustomers" && (
                          <input
                            value={item.reason || ""}
                            onChange={(event) =>
                              updateCollectionItem(
                                collection,
                                index,
                                "reason",
                                event.target.value,
                              )
                            }
                            className={smallInputClassName}
                            placeholder="Why this customer matters"
                          />
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                        {renderStatusButton(
                          collection,
                          item,
                          index,
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            removeCollectionItem(
                              collection,
                              index,
                            )
                          }
                          className="rounded-xl border border-[#f0b8a8] bg-white px-3 py-2 text-xs font-normal text-[#bd4426]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          ))}

          {saveError && (
            <p className="mt-5 rounded-2xl bg-[#fff1ec] p-4 text-sm font-light text-[#bd4426]">
              {saveError}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-2xl border border-[#bcd0ca] bg-white px-5 py-3 text-sm font-light text-[#52635f]"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => void saveMap()}
              disabled={saving}
              className="rounded-2xl bg-[#007c7a] px-6 py-3 text-sm font-normal text-white transition hover:bg-[#006b69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? "Saving business memory..."
                : "Save improvements"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
